import { useEffect, useRef, useState } from "react";
import { Mic, Square, X } from "lucide-react";
import { MAX_AUDIO_MS } from "../services/app/constants";
import { getSupportedAudioMimeType } from "../services/media/audioRecorder";
import { acceptTranscriptionConsent, hasTranscriptionConsent } from "../services/app/transcriptionConsent";
import { APP_PAUSED_EVENT } from "../services/platform/lifecycle";
import { isMobileBuild } from "../services/platform/runtime";
import { AndroidActions, isAndroidApp } from "../services/platform/androidActions";
import { recordingError } from "../services/media/recordingError";
import { createId } from "../services/app/validation";
import { useDialog } from "./useDialog";

interface AudioRecorderProps {
  onRecorded: (blob: Blob, mimeType: string, durationMs: number, recordingId: string) => void | Promise<void>;
  idleLabel?: string;
  onActivityChange?: (active: boolean) => void;
  onRecordStarted?: () => void;
  onRecordStopped?: (durationMs: number) => void;
  onPermissionDenied?: (error: unknown) => void;
  disabledReason?: string;
}

export function AudioRecorder({
  onRecorded,
  onPermissionDenied,
  onRecordStarted,
  onRecordStopped,
  disabledReason,
  idleLabel = "Record note",
  onActivityChange
}: AudioRecorderProps): JSX.Element {
  const supportedMimeType = getSupportedAudioMimeType();
  const [status, setStatus] = useState<"idle" | "recording" | "saving" | "blocked">(
    supportedMimeType ? "idle" : "blocked"
  );
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string>();
  const [awaitingConsent, setAwaitingConsent] = useState(false);
  const [starting, setStarting] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const consentDialog = useDialog<HTMLDivElement>(awaitingConsent, () => setAwaitingConsent(false));
  const startingRef = useRef(false);
  const recorderRef = useRef<MediaRecorder>();
  const streamRef = useRef<MediaStream>();
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const mountedRef = useRef(true);
  const pendingRef = useRef<{ blob: Blob; mimeType: string; durationMs: number; id: string }>();
  const [saveFailed, setSaveFailed] = useState(false);

  useEffect(() => {
    onActivityChange?.(starting || status === "recording" || status === "saving" || saveFailed);
  }, [starting, status, saveFailed, onActivityChange]);

  useEffect(() => {
    const onHidden = () => { if (document.hidden) stopRecording(); };
    window.addEventListener(APP_PAUSED_EVENT, stopRecording);
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      window.removeEventListener(APP_PAUSED_EVENT, stopRecording);
      document.removeEventListener("visibilitychange", onHidden);
    };
  }, []);

  useEffect(() => {
    if (status !== "recording") {
      return undefined;
    }
    const interval = window.setInterval(() => {
      const elapsed = Date.now() - startedAtRef.current;
      setElapsedMs(elapsed);
      if (elapsed >= MAX_AUDIO_MS) {
        stopRecording();
      }
    }, 250);
    return () => window.clearInterval(interval);
  }, [status]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (recorderRef.current?.state === "recording") {
        recorderRef.current.stop();
      }
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  async function startRecording() {
    if (disabledReason || startingRef.current || pendingRef.current) {
      return;
    }
    if (!supportedMimeType) {
      setStatus("blocked");
      return;
    }
    startingRef.current = true;
    setStarting(true);
    try {
      setError(undefined);
      setPermissionDenied(false);
      if (isMobileBuild() && !(await hasTranscriptionConsent())) {
        setAwaitingConsent(true);
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!mountedRef.current || document.hidden) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      chunksRef.current = [];
      const recorder = new MediaRecorder(stream, { mimeType: supportedMimeType });
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        void finishRecording(supportedMimeType);
      };
      startedAtRef.current = Date.now();
      recorder.start(1000);
      setElapsedMs(0);
      setStatus("recording");
      onRecordStarted?.();
    } catch (error) {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      setStatus("idle");
      const failure = recordingError(error);
      setPermissionDenied(failure.permissionDenied);
      if (failure.permissionDenied) onPermissionDenied?.(error);
      setError(failure.message);
    } finally {
      startingRef.current = false;
      setStarting(false);
    }
  }

  async function allowTranscription(): Promise<void> {
    setStarting(true);
    try {
      await acceptTranscriptionConsent();
      setAwaitingConsent(false);
      await startRecording();
    } catch {
      setError("Could not save your choice on this device. You can type the note instead.");
    } finally {
      setStarting(false);
    }
  }

  function stopRecording() {
    const recorder = recorderRef.current;
    if (recorder && recorder.state === "recording") {
      setStatus("saving");
      try {
        recorder.requestData();
      } catch {
        // Some browsers throw if no data is ready; stop still flushes available audio.
      }
      recorder.stop();
    }
  }

  async function finishRecording(mimeType: string) {
    const durationMs = Math.min(MAX_AUDIO_MS, Date.now() - startedAtRef.current);
    streamRef.current?.getTracks().forEach((track) => track.stop());
    const blob = new Blob(chunksRef.current, { type: mimeType });
    chunksRef.current = [];
    if (blob.size === 0) {
      setStatus("idle");
      setError("No audio was recorded. Try again or type the note.");
      return;
    }
    onRecordStopped?.(durationMs);
    pendingRef.current = { blob, mimeType, durationMs, id: createId("transcription_job") };
    await savePendingRecording();
  }

  async function savePendingRecording(): Promise<void> {
    const pending = pendingRef.current;
    if (!pending) return;
    setStatus("saving");
    try {
      await onRecorded(pending.blob, pending.mimeType, pending.durationMs, pending.id);
      pendingRef.current = undefined;
      setSaveFailed(false);
      setError(undefined);
    } catch {
      setSaveFailed(true);
      setError("This recording has not been saved. Keep this screen open, free some phone storage, and tap Retry save.");
    } finally {
      setStatus("idle");
      setElapsedMs(0);
    }
  }

  const seconds = Math.min(MAX_AUDIO_MS / 1000, Math.floor(elapsedMs / 1000));
  const secondsLeft = Math.max(0, MAX_AUDIO_MS / 1000 - seconds);

  if (status === "blocked") {
    return <div className="notice notice--warning">Recording is not available here. Type the note instead.</div>;
  }

  const isRecording = status === "recording";
  const buttonLabel = status === "saving" ? "Saving recording" : isRecording ? "Stop recording" : permissionDenied ? "Allow microphone" : idleLabel;
  const detail =
    status === "saving"
      ? "Saving voice note..."
      : isRecording
        ? `Recording... ${secondsLeft}s left`
        : `Up to ${MAX_AUDIO_MS / 1000} seconds`;

  return (
    <div className="recorder" data-recording-active={starting || isRecording || status === "saving" || saveFailed}>
      {awaitingConsent && (
        <div className="dialog-backdrop">
          <div ref={consentDialog} className="dialog" role="dialog" aria-modal="true" aria-labelledby="voice-consent-title" aria-describedby="voice-consent-copy" tabIndex={-1}>
            <div className="dialog__body">
              <div className="topbar">
                <h2 id="voice-consent-title">Use voice notes?</h2>
                <button className="icon-button" type="button" aria-label="Close voice permission" disabled={starting} onClick={() => setAwaitingConsent(false)}><X size={20} /></button>
              </div>
              <p id="voice-consent-copy">Your recording is sent to MoveInReport's server and OpenAI for transcription when online. Photos stay on this device. You review the words before they go in your report.</p>
              <p className="helper">Next, your phone may ask for microphone access.</p>
              <div className="button-row">
                <button className="button button--primary" type="button" disabled={starting} onClick={() => void allowTranscription()}>Continue</button>
                <button className="button" type="button" disabled={starting} onClick={() => setAwaitingConsent(false)}>Not now</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {disabledReason && <div className="notice notice--warning">{disabledReason}</div>}
      <button
        className="recorder__button"
        type="button"
        data-recording={isRecording}
        aria-label={buttonLabel}
        disabled={starting || awaitingConsent || status === "saving" || saveFailed || (!isRecording && Boolean(disabledReason))}
        onClick={() => {
          if (isRecording) {
            stopRecording();
          } else {
            void startRecording();
          }
        }}
      >
        <span className="recorder__button-copy">
          <span>{buttonLabel}</span>
          <small>{detail}</small>
        </span>
        <span className="recorder__button-icon" aria-hidden="true">
          {isRecording ? <Square size={24} /> : <Mic size={26} />}
        </span>
      </button>
      {error && <div className="notice notice--warning">{error}</div>}
      {saveFailed && <div className="button-row">
        <button className="button button--primary" type="button" disabled={status === "saving"} onClick={() => void savePendingRecording()}>Retry save</button>
        <button className="button" type="button" disabled={status === "saving"} onClick={() => {
          if (!window.confirm("Discard this unsaved recording? It cannot be recovered.")) return;
          pendingRef.current = undefined;
          setSaveFailed(false);
          setError(undefined);
        }}>Discard unsaved recording</button>
      </div>}
      {permissionDenied && isAndroidApp() && <button className="button" type="button" onClick={() => {
        void AndroidActions.openSettings().catch(() => setError("Open Settings, then Apps, MoveInReport, Permissions, and allow Microphone."));
      }}>Open app settings</button>}
    </div>
  );
}
